/**
 * Clipboard and in-app buffer for copy/cut/paste/duplicate on the canvas, plus OS paste
 * of images/text/URLs. Exposes `CopyPasteControls` for `useCanvasKeyboardShortcuts` and
 * context-menu `copyNodeIds`. Requires `useCanvas` for mutations.
 */

import { orgAPI } from "@/api/api";
import { fileRecordKeys } from "@/api/fileRecords";
import { useFileConflictResolverSafe } from "@/components/FileConflictResolver";
import { generateUniqueName } from "@/components/FileConflictResolver/uniqueName";
import { useFileRecordCreateFromURL } from "@/pages/canvas/hooks/useFileRecordCreateFromURL";
import { buildFileRecordEntityForCanvas } from "@/pages/canvas/utils/imageDimensions";
import type { FileRecord } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useReactFlow, type Edge } from "@xyflow/react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SNAP, snapToGrid } from "../canvas-controls.config";
import {
  CanvasClipboardContext,
  type CanvasClipboardEntry,
} from "../context/CanvasClipboardContext";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import {
  connectorDataWithLocalEndpoints,
  normalizeCanvasStrokeWidth,
  normalizeTextLabelFontFamily,
  normalizeTextLabelFontSize,
  resolveConnectorLocalEndpoints,
} from "../types/canvasNodeData";
import {
  clipboardRootMinPosition,
  isCopiableNode,
  prepareClipboardNodes,
  rebasePastePositions,
  sortParentFirst,
} from "../utils/copyPasteHelpers";
import { buildDetachUpdatesForSectionRemovals } from "../utils/sectionMembership";
import { useCanvasFileIngest } from "./useCanvasFileIngest";

const DUPLICATE_OFFSET = CANVAS_SNAP.grid[0];

const snap = (v: number) => snapToGrid(v, CANVAS_SNAP.grid[0]);

function sanitizePastedNodeData(
  nodeType: string,
  data: unknown,
  dimensions?: { width?: number; height?: number },
): unknown {
  if (!data || typeof data !== "object") return data;
  const d = { ...(data as Record<string, unknown>) };
  // Selection is React Flow node state (`node.selected`), not persisted in `data`.
  delete d.selected;

  if (nodeType === "canvasText") {
    if ("fontFamily" in d && typeof d.fontFamily === "string") {
      d.font_family = normalizeTextLabelFontFamily(d.fontFamily);
      delete d.fontFamily;
    }
    if ("fontSize" in d && typeof d.fontSize === "string") {
      d.font_size = normalizeTextLabelFontSize(d.fontSize);
      delete d.fontSize;
    }
    if (typeof d.font_family === "string") {
      d.font_family = normalizeTextLabelFontFamily(d.font_family);
    }
    if (typeof d.font_size === "string") {
      d.font_size = normalizeTextLabelFontSize(d.font_size);
    }
    return d;
  }

  if (nodeType === "canvasShape") {
    d.stroke_width = normalizeCanvasStrokeWidth(d.stroke_width);
    return d;
  }

  if (nodeType === "canvasConnector") {
    d.stroke_width = normalizeCanvasStrokeWidth(d.stroke_width);
    const width =
      typeof dimensions?.width === "number" && Number.isFinite(dimensions.width)
        ? dimensions.width
        : 150;
    const height =
      typeof dimensions?.height === "number" &&
      Number.isFinite(dimensions.height)
        ? dimensions.height
        : 1;
    const { start, end } = resolveConnectorLocalEndpoints(d, width, height);
    return connectorDataWithLocalEndpoints(d, start, end);
  }

  return d;
}

const PASTEABLE_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);

export function freshId(_type: string): string {
  return uuidv4();
}

/** Edges whose source and target are both in the copied node set (so they travel with them). */
function edgesBetween(edges: Edge[], nodeIds: Set<string>): Edge[] {
  return edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
}

/**
 * File-record id of an entity node that should be true-copied when pasted into a
 * *different* canvas (a Cohesive-stored `file_record` with a real id), else null.
 * Other entities (skills, external integrations) copy by reference.
 */
function copyableFileRecordId(node: any): string | null {
  if (node?.type !== "entity") return null;
  const entity = node?.data?.entity;
  if (!entity || entity.base !== "file_record") return null;
  const id = entity?.data?.id;
  return typeof id === "string" && id.length > 0 ? id : null;
}

/** Loading-ghost entity shown while a cross-canvas file copy is in flight. */
function ghostFileRecordEntity(node: any) {
  const data = node?.data?.entity?.data ?? {};
  return {
    type: "file_record" as const,
    base: "file_record" as const,
    platform: "cohesive" as const,
    data: {
      id: "",
      title: typeof data.title === "string" ? data.title : "Untitled",
      ...(typeof data.content_type === "string"
        ? { content_type: data.content_type }
        : {}),
    },
  };
}

export interface CopyPasteControls {
  copy: () => void;
  copyNodeIds: (ids: string[]) => void;
  cut: () => void;
  /** Paste the clipboard at the cursor (⌘V). Consumes the buffer. */
  paste: () => void;
  /** Paste the clipboard at a specific flow position (e.g. quick-add menu cursor). */
  pasteAt: (flowPosition: { x: number; y: number }) => void;
  duplicate: () => void;
  /** Duplicate specific nodes by id (e.g. context menu) — same rules as duplicate. */
  duplicateNodeIds: (ids: string[]) => void;
  canPaste: boolean;
}

export function useCopyPaste(options?: {
  /**
   * Register the global (window) `paste` listener that drops OS-clipboard
   * content (images / URLs / text) onto the canvas. This MUST be enabled by
   * exactly one consumer — the `CanvasPage`. Enabling it per node would add a
   * separate window listener for every mounted node, and since `preventDefault`
   * doesn't stop sibling listeners, a single paste would run the handler once
   * per node and create a (canvas-content-dependent) number of duplicate pastes.
   * Defaults to false so node/toolbar consumers that only need copy/duplicate
   * never register the listener.
   */
  registerOsPaste?: boolean;
}): CopyPasteControls {
  const registerOsPaste = options?.registerOsPaste ?? false;
  const { getNodes, getEdges, getInternalNode, screenToFlowPosition } =
    useReactFlow();
  const { canvasId } = useCanvasRoute();
  const {
    canEdit,
    addNode,
    addEdge,
    removeNode,
    removeNodes,
    replaceNodeData,
    updateNodeDimensions,
    batchUpdateNodeParents,
  } = useCanvas();

  const ingestURL = useFileRecordCreateFromURL();
  const { ingestFiles } = useCanvasFileIngest(canvasId);
  const queryClient = useQueryClient();
  // Null in headless contexts (no provider) → copy without a conflict prompt.
  const conflictResolver = useFileConflictResolverSafe();

  // Workspace-level clipboard (survives canvas switches). Falls back to a
  // component-local buffer when no provider is mounted (e.g. headless capture),
  // preserving the original per-instance behaviour there.
  const clipboard = useContext(CanvasClipboardContext);
  const [localEntries, setLocalEntries] = useState<CanvasClipboardEntry[]>([]);
  const latest = clipboard ? clipboard.latest : (localEntries[0] ?? null);
  const pushEntry = useCallback(
    (entry: CanvasClipboardEntry) => {
      if (clipboard) clipboard.push(entry);
      else setLocalEntries([entry]);
    },
    [clipboard],
  );

  const clearClipboard = useCallback(() => {
    if (clipboard) clipboard.clear();
    else setLocalEntries([]);
  }, [clipboard]);

  const mousePosRef = useRef({ x: 0, y: 0 });

  // Whether the canvas buffer actually has nodes to paste (an entry with an
  // empty `nodes` array would otherwise make ⌘V preventDefault + no-op, blocking
  // the native OS-paste fallback). A ref so the native paste listener reads it
  // without re-registering on every render.
  const hasBufferedNodes = latest != null && latest.nodes.length > 0;
  const canPasteRef = useRef(false);
  canPasteRef.current = hasBufferedNodes;

  // Track screen-space mouse position for paste-at-cursor behaviour. Only the
  // OS-paste owner needs this (it feeds the native paste listener and `paste()`),
  // so it's gated to avoid a redundant per-node window listener.
  useEffect(() => {
    if (!registerOsPaste) return;
    const onMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onMouseMove);
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, [registerOsPaste]);

  // ---- cross-canvas file copy (with conflict resolution) ----
  // File-backed entity nodes pasted into a DIFFERENT canvas become true copies
  // (new FileRecord + S3 object under this canvas). When a record already exists
  // at the destination path, the shared file-conflict dialog reconciles it —
  // Skip / Replace contents / Keep both — the same UX as uploads & moves. The
  // resolver returns the concrete copy ops (renamed for keep-both, overwrite for
  // replace, dropped for skip), or null when the user cancels the whole batch.
  const copyFileRecordNodes = useCallback(
    async (
      items: Array<{
        node: any;
        newId: string;
        newParentId: string | undefined;
        position: { x: number; y: number };
        sourceId: string;
      }>,
      destCanvasId: string,
      sourceCanvasId: string | null,
    ) => {
      const placeGhostAndCopy = (
        item: (typeof items)[number],
        sourceFileRecordId: string,
        payload: Parameters<typeof orgAPI.fileRecords.copy>[1],
      ) => {
        addNode({
          ...item.node,
          id: item.newId,
          parentId: item.newParentId,
          position: item.position,
          selected: false,
          data: { loading: true, entity: ghostFileRecordEntity(item.node) },
        } as Parameters<typeof addNode>[0]);
        void (async () => {
          try {
            const copied = await orgAPI.fileRecords.copy(
              sourceFileRecordId,
              payload,
            );
            queryClient.setQueryData(fileRecordKeys.detail(copied.id), copied);
            // Keep the destination canvas list in sync so the file shows in the
            // rail immediately and a subsequent paste's conflict check sees it.
            queryClient.setQueryData(
              fileRecordKeys.canvas(destCanvasId),
              (prev: FileRecord[] | undefined) => {
                if (!prev) return [copied];
                const idx = prev.findIndex((r) => r.id === copied.id);
                if (idx === -1) return [...prev, copied];
                const next = prev.slice();
                next[idx] = { ...prev[idx], ...copied };
                return next;
              },
            );
            const { entity, nodeWidth, nodeHeight } =
              buildFileRecordEntityForCanvas({ record: copied });
            replaceNodeData(item.newId, { entity });
            updateNodeDimensions(item.newId, {
              width: nodeWidth,
              height: nodeHeight,
            });
          } catch {
            removeNode(item.newId);
            // Shared id collapses repeated failures in a multi-file paste into
            // one toast instead of N.
            toast.error("Failed to copy file to this canvas", {
              id: "canvas-cross-copy-error",
            });
          }
        })();
      };

      // Resolve each source's path (the copy endpoint requires a matching
      // extension; the source path supplies both the name and that extension).
      // Prefer the cached record (we were just on the source canvas) over a fetch.
      const withSource = await Promise.all(
        items.map(async (item) => {
          const cached = queryClient.getQueryData<FileRecord>(
            fileRecordKeys.detail(item.sourceId),
          );
          if (cached) return { item, src: cached };
          try {
            return {
              item,
              src: await orgAPI.fileRecords.getById(item.sourceId),
            };
          } catch {
            return { item, src: null as FileRecord | null };
          }
        }),
      );
      const placeable = withSource.filter(
        (x): x is { item: (typeof items)[number]; src: FileRecord } =>
          x.src != null,
      );
      if (placeable.length === 0) return;

      // Same-canvas duplicate/paste: a file has at most one node per canvas, so
      // this is a genuinely new file_record — a renamed copy ("chart (1).png").
      // The conflict resolver treats same-scope copies as no-ops, so we generate
      // a unique name against the canvas's existing files and copy directly.
      const isSameCanvas = !sourceCanvasId || sourceCanvasId === destCanvasId;
      if (isSameCanvas) {
        const existing =
          queryClient.getQueryData<FileRecord[]>(
            fileRecordKeys.canvas(destCanvasId),
          ) ?? [];
        const taken = new Set(existing.map((r) => r.path));
        for (const { item, src } of placeable) {
          const uniquePath = generateUniqueName(src.path, false, taken);
          taken.add(uniquePath);
          placeGhostAndCopy(item, item.sourceId, {
            destination: {
              class: "canvas",
              canvas_id: destCanvasId,
              path: uniquePath,
            },
          });
        }
        return;
      }

      // No resolver (e.g. headless capture) → copy without prompting.
      if (!conflictResolver) {
        for (const { item, src } of placeable) {
          placeGhostAndCopy(item, item.sourceId, {
            destination: {
              class: "canvas",
              canvas_id: destCanvasId,
              path: src.path,
            },
          });
        }
        return;
      }

      const ops = await conflictResolver.resolveCopies({
        copies: placeable.map(({ item, src }) => ({
          fileRecordId: item.sourceId,
          sourceName: src.path.split("/").pop() || src.path,
          isFolder: false,
          source: {
            class: "canvas" as const,
            canvas_id: sourceCanvasId ?? "",
            path: src.path,
          },
          destination: {
            components: { class: "canvas" as const, canvas_id: destCanvasId },
            parentPath: "",
          },
        })),
        sourceRecords: placeable.map(({ src }) => src),
      });
      // Cancelled the whole batch → place nothing.
      if (!ops) return;

      // Assign ops back to nodes by source id, in order (so the same file pasted
      // more than once maps 1:1). Items with no op were skipped — no node added.
      const pending = new Map<string, Array<(typeof placeable)[number]>>();
      for (const p of placeable) {
        const arr = pending.get(p.item.sourceId) ?? [];
        arr.push(p);
        pending.set(p.item.sourceId, arr);
      }
      for (const op of ops) {
        const next = pending.get(op.sourceFileRecordId)?.shift();
        if (!next) continue;
        placeGhostAndCopy(next.item, op.sourceFileRecordId, op.payload);
      }
    },
    [
      conflictResolver,
      addNode,
      removeNode,
      replaceNodeData,
      updateNodeDimensions,
      queryClient,
    ],
  );

  // ---- core paste logic (shared by paste + duplicate) ----
  // `opts.sourceCanvasId` drives the cross-canvas case: file-backed entity nodes
  // are true-copied (new FileRecord + S3 object under the destination canvas)
  // rather than referencing the source canvas's record (whose S3 path embeds the
  // source canvas id). Same-canvas paste/duplicate keeps the cheap reference copy.
  const pasteNodes = useCallback(
    (
      nodes: any[],
      edges: Edge[],
      pastePos: { x: number; y: number },
      opts?: { sourceCanvasId?: string | null },
    ) => {
      if (!canEdit || nodes.length === 0) return;

      const destCanvasId = canvasId;
      const ordered = sortParentFirst(nodes);
      const positions = rebasePastePositions(ordered, pastePos, snap);

      const idMap = new Map<string, string>();
      ordered.forEach((n: any) => idMap.set(n.id, freshId(n.type)));

      // File-backed entity nodes going cross-canvas are deferred to
      // `copyFileRecordNodes` so one conflict dialog can reconcile the whole
      // batch before any of those nodes is placed.
      const pendingFileCopies: Array<{
        node: any;
        newId: string;
        newParentId: string | undefined;
        position: { x: number; y: number };
        sourceId: string;
      }> = [];

      ordered.forEach((node: any) => {
        const newId = idMap.get(node.id)!;
        const newParentId = node.parentId
          ? (idMap.get(node.parentId) ?? undefined)
          : undefined;
        const position = positions.get(node.id) ?? {
          x: node.position.x,
          y: node.position.y,
        };

        // A file can have only one node per canvas, so every paste/duplicate of a
        // file-backed node — same-canvas or cross-canvas — is a true copy (a new
        // file_record), never a second node onto the same record.
        const sourceFileRecordId = copyableFileRecordId(node);
        if (sourceFileRecordId && destCanvasId) {
          pendingFileCopies.push({
            node,
            newId,
            newParentId,
            position,
            sourceId: sourceFileRecordId,
          });
          return;
        }

        addNode({
          ...node,
          id: newId,
          data: sanitizePastedNodeData(node.type, node.data, {
            width: node.width,
            height: node.height,
          }),
          position,
          parentId: newParentId,
          selected: false,
        });
      });

      // Re-add connecting edges, remapped to the new node ids. Endpoints touching
      // a deferred (async) file-record copy are skipped — that node may never
      // land (skipped in conflict resolution), which would orphan the edge.
      const deferredIds = new Set(pendingFileCopies.map((p) => p.node.id));
      edges.forEach((edge) => {
        const newSource = idMap.get(edge.source);
        const newTarget = idMap.get(edge.target);
        if (
          !newSource ||
          !newTarget ||
          deferredIds.has(edge.source) ||
          deferredIds.has(edge.target)
        )
          return;
        addEdge({
          id: freshId("edge"),
          source: newSource,
          target: newTarget,
          type: edge.type,
          sourceHandle: edge.sourceHandle ?? undefined,
          targetHandle: edge.targetHandle ?? undefined,
          data: edge.data as Record<string, unknown> | undefined,
          style: edge.style,
          markerStart: edge.markerStart,
          markerEnd: edge.markerEnd,
          label: edge.label,
        });
      });

      if (pendingFileCopies.length > 0 && destCanvasId) {
        void copyFileRecordNodes(
          pendingFileCopies,
          destCanvasId,
          opts?.sourceCanvasId ?? null,
        );
      }
    },
    [canEdit, addNode, addEdge, canvasId, copyFileRecordNodes],
  );

  // ---- copy ----
  const copy = useCallback(() => {
    const allNodes = getNodes();
    const selected = allNodes.filter((n) => n.selected && isCopiableNode(n));
    if (selected.length === 0) return;
    const copiable = prepareClipboardNodes(selected, allNodes);
    const ids = new Set(copiable.map((n) => n.id));
    pushEntry({
      nodes: copiable,
      edges: edgesBetween(getEdges(), ids),
      sourceCanvasId: canvasId ?? null,
      copiedAt: Date.now(),
    });
  }, [getNodes, getEdges, pushEntry, canvasId]);

  /** Copy specific nodes by id (e.g. context menu) — same rules as multi-select copy. */
  const copyNodeIds = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      const allNodes = getNodes();
      const selected = allNodes.filter(
        (n) => idSet.has(n.id) && isCopiableNode(n),
      );
      if (selected.length === 0) return;
      const copiable = prepareClipboardNodes(selected, allNodes);
      const copiedIds = new Set(copiable.map((n) => n.id));
      pushEntry({
        nodes: copiable,
        edges: edgesBetween(getEdges(), copiedIds),
        sourceCanvasId: canvasId ?? null,
        copiedAt: Date.now(),
      });
    },
    [getNodes, getEdges, pushEntry, canvasId],
  );

  // ---- cut ----
  const cut = useCallback(() => {
    if (!canEdit) return;
    const allNodes = getNodes();
    const selected = allNodes.filter((n) => n.selected && isCopiableNode(n));
    if (selected.length === 0) return;
    const copiable = prepareClipboardNodes(selected, allNodes);
    const ids = new Set(copiable.map((n) => n.id));
    pushEntry({
      nodes: copiable,
      edges: edgesBetween(getEdges(), ids),
      sourceCanvasId: canvasId ?? null,
      copiedAt: Date.now(),
    });
    const nodeIds = copiable.map((n) => n.id);
    // Children included in the cut set are removed with the section; detach
    // only applies to nodes that remain on the canvas under a removed section.
    const detach = buildDetachUpdatesForSectionRemovals(
      nodeIds,
      allNodes,
      getInternalNode,
    );
    if (detach.length > 0) {
      batchUpdateNodeParents(detach);
    }
    removeNodes(nodeIds);
  }, [
    canEdit,
    getNodes,
    getEdges,
    getInternalNode,
    batchUpdateNodeParents,
    removeNodes,
    pushEntry,
    canvasId,
  ]);

  // ---- system-clipboard placement helpers (shared by ⌘V and the menu) ----
  // Upload pasted image bytes as canvas file nodes, cascading downward from
  // `flowPos`, through the shared ingestion pipeline. Pasted images are
  // auto-named blobs, so we skip the conflict resolver here (no Skip/Replace
  // prompt for a quick paste) — the queue still disambiguates duplicate names.
  const placeImageFilesAtPosition = useCallback(
    async (files: File[], flowPos: { x: number; y: number }) => {
      await ingestFiles(files, {
        placement: { strategy: "stack", at: flowPos },
        resolveConflicts: false,
        errorMessage: "Failed to paste image",
      });
    },
    [ingestFiles],
  );

  // A single-line HTTPS URL is ingested as a file record; anything else becomes
  // a text label.
  const placeTextAtPosition = useCallback(
    (text: string, flowPos: { x: number; y: number }) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const pos = { x: snap(flowPos.x), y: snap(flowPos.y) };
      const isURL =
        !trimmed.includes("\n") &&
        (trimmed.startsWith("http://") || trimmed.startsWith("https://"));
      // URL ingestion needs a canvas; fall back to a text node without one.
      if (isURL && canvasId) {
        ingestURL({ sourceUrl: trimmed, position: pos });
        return;
      }
      addNode({
        id: uuidv4(),
        type: "canvasText" as const,
        position: pos,
        width: 400,
        data: {
          content: trimmed,
          font_family: "Inter, sans-serif",
          font_size: "20px",
        },
      });
    },
    [canvasId, ingestURL, addNode],
  );

  // Read the system clipboard and place its contents at `flowPos`. Used by the
  // quick-add menu's Paste (a click can't read a ClipboardEvent). Unlike ⌘V's
  // native paste event, navigator.clipboard can't see Finder-copied files —
  // those still come through ⌘V / drag-drop.
  const pasteOsClipboardAt = useCallback(
    async (flowPos: { x: number; y: number }) => {
      if (!canEdit || !canvasId) return;
      const clip = navigator.clipboard;

      const imageFiles: File[] = [];
      if (clip?.read) {
        try {
          const items = await clip.read();
          for (const item of items) {
            const type = item.types.find((t) => PASTEABLE_IMAGE_TYPES.has(t));
            if (!type) continue;
            try {
              const blob = await item.getType(type);
              imageFiles.push(
                new File(
                  [blob],
                  `pasted-image-${Date.now()}.${type.split("/")[1]}`,
                  {
                    type,
                  },
                ),
              );
            } catch {
              // skip unreadable item
            }
          }
        } catch {
          // read denied/unavailable — fall through to text
        }
      }
      if (imageFiles.length > 0) {
        await placeImageFilesAtPosition(imageFiles, flowPos);
        return;
      }

      let text = "";
      try {
        text = (await clip?.readText?.()) ?? "";
      } catch {
        text = "";
      }
      if (text.trim()) {
        placeTextAtPosition(text, flowPos);
        return;
      }
      toast("Nothing to paste");
    },
    [canEdit, canvasId, placeImageFilesAtPosition, placeTextAtPosition],
  );

  // ---- paste canvas nodes (at cursor) ----
  // Paste consumes the buffer so a subsequent ⌘V falls through to the native
  // paste event (system clipboard). To paste into another canvas, copy again.
  const paste = useCallback(() => {
    if (!latest || latest.nodes.length === 0) return;
    pasteNodes(
      latest.nodes,
      latest.edges,
      screenToFlowPosition(mousePosRef.current),
      { sourceCanvasId: latest.sourceCanvasId },
    );
    clearClipboard();
  }, [latest, screenToFlowPosition, pasteNodes, clearClipboard]);

  // Paste at an explicit flow position (quick-add menu) rather than the cursor —
  // the pointer is on the menu item when the action fires, not the canvas. Falls
  // back to the system clipboard when the canvas buffer is empty.
  const pasteAt = useCallback(
    (flowPosition: { x: number; y: number }) => {
      if (latest && latest.nodes.length > 0) {
        pasteNodes(latest.nodes, latest.edges, flowPosition, {
          sourceCanvasId: latest.sourceCanvasId,
        });
        clearClipboard();
        return;
      }
      void pasteOsClipboardAt(flowPosition);
    },
    [latest, pasteNodes, clearClipboard, pasteOsClipboardAt],
  );

  // ---- duplicate ----
  const duplicate = useCallback(() => {
    if (!canEdit) return;
    const allNodes = getNodes();
    const selected = allNodes.filter(
      (n) => n.selected && n.type !== "chatSession",
    );
    if (selected.length === 0) return;
    const copiable = prepareClipboardNodes(selected, allNodes);
    const { x: minX, y: minY } = clipboardRootMinPosition(copiable);
    const ids = new Set(copiable.map((n) => n.id));
    // Same-canvas duplicate: no sourceCanvasId → reference copy (unchanged).
    pasteNodes(copiable, edgesBetween(getEdges(), ids), {
      x: minX + DUPLICATE_OFFSET,
      y: minY + DUPLICATE_OFFSET,
    });
  }, [canEdit, getNodes, getEdges, pasteNodes]);

  /** Duplicate specific nodes by id (context menu) — same rules as duplicate. */
  const duplicateNodeIds = useCallback(
    (ids: string[]) => {
      if (!canEdit || ids.length === 0) return;
      const idSet = new Set(ids);
      const allNodes = getNodes();
      const selected = allNodes.filter(
        (n) => idSet.has(n.id) && n.type !== "chatSession",
      );
      if (selected.length === 0) return;
      const copiable = prepareClipboardNodes(selected, allNodes);
      const { x: minX, y: minY } = clipboardRootMinPosition(copiable);
      const dupIds = new Set(copiable.map((n) => n.id));
      pasteNodes(copiable, edgesBetween(getEdges(), dupIds), {
        x: minX + DUPLICATE_OFFSET,
        y: minY + DUPLICATE_OFFSET,
      });
    },
    [canEdit, getNodes, getEdges, pasteNodes],
  );

  // ---- native paste event: images → URL → text ----
  //
  // Fires only when the canvas buffer is empty (the ⌘V keydown pastes buffered
  // nodes first and preventDefaults). clipboardData includes Finder/desktop
  // files, which navigator.clipboard.read() omits — so this is the only path
  // that can paste them. Reuses the shared placement helpers via refs so the
  // listener registers once.
  const placeImageRef = useRef(placeImageFilesAtPosition);
  placeImageRef.current = placeImageFilesAtPosition;
  const placeTextRef = useRef(placeTextAtPosition);
  placeTextRef.current = placeTextAtPosition;

  useEffect(() => {
    if (!registerOsPaste) return;
    const onPaste = (e: ClipboardEvent) => {
      if (!canEdit) return;
      if (canPasteRef.current) return;

      const active = document.activeElement as HTMLElement | null;
      if (
        active?.isContentEditable ||
        active?.tagName === "INPUT" ||
        active?.tagName === "TEXTAREA"
      )
        return;

      const flowPos = screenToFlowPosition(mousePosRef.current);

      // --- 1. Image files ---
      // Extract synchronously — DataTransferItem references are invalidated once
      // the handler yields, so build the File objects before any async work.
      const files: File[] = [];
      for (const item of Array.from(e.clipboardData?.items ?? [])) {
        if (item.kind !== "file" || !PASTEABLE_IMAGE_TYPES.has(item.type))
          continue;
        const raw = item.getAsFile();
        if (!raw) continue;
        files.push(
          new File(
            [raw],
            raw.name && raw.name !== "image.png"
              ? raw.name
              : `pasted-image-${Date.now()}.${raw.type.split("/")[1]}`,
            { type: raw.type },
          ),
        );
      }
      if (files.length > 0) {
        e.preventDefault();
        void placeImageRef.current(files, flowPos);
        return;
      }

      // --- 2. URL or text ---
      const text = e.clipboardData?.getData("text/plain")?.trim();
      if (!text) return;
      e.preventDefault();
      placeTextRef.current(text, flowPos);
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [canEdit, screenToFlowPosition, registerOsPaste]);

  return {
    copy,
    copyNodeIds,
    cut,
    paste,
    pasteAt,
    duplicate,
    duplicateNodeIds,
    canPaste: hasBufferedNodes,
  };
}
